import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination,
  Paper, 
  CircularProgress,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  TextField,
  Grid,
  Card,
  CardContent,
  Avatar,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Dashboard, 
  People, 
  ExitToApp,
  FilterList,
  Refresh,
  DateRange,
  Search,
  SettingsOutlined,
  MenuOpen,
  GetApp,
  Message,
  ChevronLeft,
  Close
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie } from 'recharts';

function AdminPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messagesError, setMessagesError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isMobile || isTablet);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const conversationRef = useRef(null);
  const [messagesSearchQuery, setMessagesSearchQuery] = useState('');
  const [filteredMessages, setFilteredMessages] = useState([]);

  // Update sidebar collapsed state when screen size changes
  useEffect(() => {
    setSidebarCollapsed(isMobile || isTablet);
  }, [isMobile, isTablet]);

  // Get user info from localStorage
  const userInfo = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // API base URL for production or development
      const API_BASE_URL = process.env.NODE_ENV === 'production' 
        ? 'https://chatbot-backend-21oh.onrender.com' 
        : 'http://localhost:5000';
      
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }
      
      // Use auth token with API request
      const leadsResponse = await fetch(`${API_BASE_URL}/api/admin/leads`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!leadsResponse.ok) {
        const errorText = await leadsResponse.text();
        
        // If unauthorized, redirect to login
        if (leadsResponse.status === 401) {
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('token');
          navigate('/admin/login');
          throw new Error('Your session has expired. Please login again.');
        }
        
        throw new Error(`Leads API error: ${leadsResponse.status} - ${errorText}`);
      }
      
      const leadsData = await leadsResponse.json();
      
      // Ensure leads data is an array
      const leadsArray = Array.isArray(leadsData) ? leadsData : [];
      setLeads(leadsArray);
      setFilteredLeads(leadsArray);
      setLoading(false);
    } catch (err) {
      setError(`Failed to fetch data: ${err.message}`);
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      setMessagesLoading(true);
      setMessagesError(null);
      
      // API base URL for production or development
      const API_BASE_URL = process.env.NODE_ENV === 'production' 
        ? 'https://chatbot-backend-21oh.onrender.com' 
        : 'http://localhost:5000';
      
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }
      
      // Use auth token with API request
      const messagesResponse = await fetch(`${API_BASE_URL}/api/admin/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!messagesResponse.ok) {
        const errorText = await messagesResponse.text();
        
        // If unauthorized, redirect to login
        if (messagesResponse.status === 401) {
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('token');
          navigate('/admin/login');
          throw new Error('Your session has expired. Please login again.');
        }
        
        throw new Error(`Messages API error: ${messagesResponse.status} - ${errorText}`);
      }
      
      const messagesData = await messagesResponse.json();
      
      // Ensure messages data is an array and has default values if needed
      const messagesArray = Array.isArray(messagesData) ? messagesData.map(msg => ({
        ...msg,
        messageCount: msg.messageCount || (msg.messages ? msg.messages.length : 0)
      })) : [];
      
      setMessages(messagesArray);
      setFilteredMessages(messagesArray);
      setMessagesLoading(false);
    } catch (err) {
      setMessagesError(`Failed to fetch messages: ${err.message}`);
      setMessagesLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchMessages(); // Fetch messages immediately on component load
    
    // If we switch to messages section, ensure data is refreshed
    if (activeMenu === 'messages') {
      fetchMessages();
    }
    
    // Update filteredMessages when messages change
    setFilteredMessages(messages);
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMenu, messages.length]);

  // Scroll to conversation view when a conversation is selected
  useEffect(() => {
    if (selectedConversation && conversationRef.current) {
      conversationRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedConversation]);

  // Apply filters when search or date filters change
  useEffect(() => {
    let result = [...leads];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(lead => 
        (lead.name && lead.name.toLowerCase().includes(query)) ||
        (lead.email && lead.email.toLowerCase().includes(query)) ||
        (lead.phone && lead.phone.includes(query)) ||
        (lead.organization && lead.organization.toLowerCase().includes(query))
      );
    }
    
    // Apply date filter
    if (dateFilter.startDate) {
      const startDate = new Date(dateFilter.startDate);
      result = result.filter(lead => new Date(lead.timestamp) >= startDate);
    }
    
    if (dateFilter.endDate) {
      const endDate = new Date(dateFilter.endDate);
      // Set end date to the end of the day
      endDate.setHours(23, 59, 59, 999);
      result = result.filter(lead => new Date(lead.timestamp) <= endDate);
    }
    
    setFilteredLeads(result);
  }, [leads, searchQuery, dateFilter]);

  // Update useEffect to handle messages filtering
  useEffect(() => {
    if (messages.length > 0) {
      let result = [...messages];
      
      // Apply search filter for messages
      if (messagesSearchQuery) {
        const query = messagesSearchQuery.toLowerCase();
        result = result.filter(message => 
          (message.sessionId && message.sessionId.toLowerCase().includes(query))
        );
      }
      
      setFilteredMessages(result);
    } else {
      setFilteredMessages([]);
    }
  }, [messages, messagesSearchQuery]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin/login');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleFilter = () => {
    setFilterOpen(!filterOpen);
  };

  const resetFilters = () => {
    setSearchQuery('');
    setDateFilter({
      startDate: '',
      endDate: ''
    });
    setFilteredLeads(leads);
  };

  const handleMenuClick = (menuId) => {
    setActiveMenu(menuId);
    setSelectedConversation(null);
    if (menuId === 'messages') {
      fetchMessages();
    }
    // Close filter when changing sections
    setFilterOpen(false);
  };

  // Sidebar width based on collapsed state
  const sidebarWidth = sidebarCollapsed ? 80 : 260;

  // Function to export leads to CSV
  const exportToCSV = () => {
    if (filteredLeads.length === 0) {
      return;
    }
    
    // Define CSV headers
    const headers = [
      'Name',
      'Email',
      'Phone',
      'Organization',
      'Department',
      'City',
      'User Type',
      'Date'
    ];
    
    // Convert leads data to CSV rows
    const csvRows = filteredLeads.map(lead => [
      lead.name || '',
      lead.email || '',
      lead.phone || '',
      lead.organization || '',
      lead.department || '',
      lead.city || '',
      lead.userType || '',
      new Date(lead.timestamp).toLocaleString() || ''
    ]);
    
    // Add headers to the beginning
    csvRows.unshift(headers);
    
    // Convert each row to CSV format and join with newlines
    const csvContent = csvRows.map(row => 
      row.map(cell => 
        // Escape commas, quotes, etc.
        typeof cell === 'string' ? `"${cell.replace(/"/g, '""')}"` : cell
      ).join(',')
    ).join('\n');
    
    // Create a Blob with the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create URL for the Blob
    const url = URL.createObjectURL(blob);
    
    // Create a link element to trigger download
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `leads_export_${new Date().toISOString().slice(0,10)}.csv`);
    link.style.visibility = 'hidden';
    
    // Add to document, click to download, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Add date formatting helper function
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.toLocaleString('default', { month: 'short' }).toUpperCase();
    const day = date.getDate();
    const year = date.getFullYear();
    return { month, day, year };
  };

  // Convert ISO date to readable format
  const formatChatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  // Calculate chat duration
  const calculateChatDuration = (messages) => {
    if (!messages || messages.length < 2) return 'N/A';
    
    try {
      const firstMessage = new Date(messages[0].timestamp);
      const lastMessage = new Date(messages[messages.length - 1].timestamp);
      
      const durationMs = lastMessage - firstMessage;
      if (isNaN(durationMs)) return 'N/A';
      
      const durationMins = Math.floor(durationMs / 60000);
      const durationSecs = Math.floor((durationMs % 60000) / 1000);
      
      return `${durationMins}m ${durationSecs}s`;
    } catch (error) {
      console.error('Error calculating chat duration:', error);
      return 'N/A';
    }
  };

  // Function to handle when view conversation is clicked
  const handleViewConversation = (chatSession) => {
    // Log each message for debugging
    if (chatSession.messages && chatSession.messages.length > 0) {
      chatSession.messages.forEach((msg, idx) => {
        console.log(`Message ${idx}:`, JSON.stringify(msg));
      });
    }
    
    setSelectedConversation(chatSession);
    // Hide messages section content when viewing a conversation
    document.getElementById('messages-section-content').style.display = 'none';
    document.getElementById('conversation-view').style.display = 'flex';
  };

  // Function to handle back from conversation view
  const handleBackFromConversation = () => {
    setSelectedConversation(null);
    document.getElementById('messages-section-content').style.display = 'block';
    document.getElementById('conversation-view').style.display = 'none';
  };

  // Add console log to debug data
  useEffect(() => {
    // Ensure filteredMessages is always updated when messages change
    setFilteredMessages(messages);
  }, [messages, leads]);

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          width: { xs: '100%', sm: `calc(100% - ${sidebarWidth}px)` }, 
          ml: { xs: 0, sm: `${sidebarWidth}px` },
          boxShadow: 'none',
          backgroundColor: '#fff',
          color: '#333',
          borderBottom: '1px solid #e0e0e0',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            {isMobile && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={toggleSidebar}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            
            {activeMenu !== 'dashboard' && ( // Only show filter button outside of dashboard
              <IconButton
                color="inherit"
                edge="start"
                onClick={toggleFilter}
                sx={{ mr: 2 }}
              >
                <FilterList />
              </IconButton>
            )}
            
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" component="div" color="primary">
                {activeMenu === 'dashboard' && 'Dashboard'}
                {activeMenu === 'leads' && 'Leads Management'}
                {activeMenu === 'messages' && 'Chat Messages'}
                {activeMenu === 'analytics' && 'Analytics'}
                {activeMenu === 'settings' && 'Settings'}
              </Typography>
            </Box>
            
            <Stack direction="row" spacing={1}>
              <IconButton color="inherit" onClick={handleMenuOpen}>
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32,
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    fontSize: '0.875rem'
                  }}
                >
                  {userInfo.firstName ? userInfo.firstName[0] : 'A'}
                </Avatar>
              </IconButton>
            </Stack>
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: { minWidth: 180, backgroundColor: '#fff' }
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {userInfo.firstName ? `${userInfo.firstName} ${userInfo.lastName}` : 'Admin User'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {userInfo.email || 'admin@example.com'}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={() => handleMenuClick('settings')}>
                <ListItemIcon>
                  <SettingsOutlined fontSize="small" />
                </ListItemIcon>
                Settings
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <ExitToApp fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: sidebarWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          position: 'fixed',
          height: '100vh',
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          boxShadow: 3,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          zIndex: { xs: 1300, sm: 1200 }, // Higher z-index for mobile
          display: { xs: sidebarCollapsed ? 'none' : 'flex', sm: 'flex' }, // Hide on mobile when collapsed
          flexDirection: 'column'
        }}
      >
        {/* Sidebar Header with Logo */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: sidebarCollapsed ? 'center' : 'space-between',
            padding: sidebarCollapsed ? 2 : '16px 20px',
            height: 64,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          {!sidebarCollapsed && (
            <Typography variant="h6" fontWeight="bold" noWrap component="div">
              Admin Panel
            </Typography>
          )}
          <IconButton
            onClick={toggleSidebar}
            sx={{ 
              color: 'white',
              padding: sidebarCollapsed ? 1 : 0,
              display: { xs: 'none', sm: 'block' } // Hide on mobile, we'll use the AppBar button instead
            }}
          >
            {sidebarCollapsed ? <MenuIcon /> : <MenuOpen />}
          </IconButton>
        </Box>
        
        {/* User Info */}
        {!sidebarCollapsed && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', mr: 2, color: 'white' }}>
              {userInfo.firstName ? userInfo.firstName[0] : 'A'}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {userInfo.firstName ? `${userInfo.firstName} ${userInfo.lastName}` : 'Admin User'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                {userInfo.email || 'admin@example.com'}
              </Typography>
            </Box>
          </Box>
        )}
        
        {/* Menu Items */}
        <List component="nav" sx={{ pt: 2, flexGrow: 1 }}>
          <ListItem 
            button 
            selected={activeMenu === 'dashboard'} 
            onClick={() => handleMenuClick('dashboard')}
            sx={{ 
              mb: 1,
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
              px: sidebarCollapsed ? 2 : 3,
              py: 1.5,
              borderRadius: sidebarCollapsed ? 0 : '0 25px 25px 0',
              backgroundColor: activeMenu === 'dashboard' ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                },
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 40, color: 'white' }}>
              <Dashboard />
            </ListItemIcon>
            {!sidebarCollapsed && <ListItemText primary="Dashboard" />}
          </ListItem>
          
          <ListItem 
            button 
            selected={activeMenu === 'leads'} 
            onClick={() => handleMenuClick('leads')}
            sx={{ 
              mb: 1,
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
              px: sidebarCollapsed ? 2 : 3,
              py: 1.5,
              borderRadius: sidebarCollapsed ? 0 : '0 25px 25px 0',
              backgroundColor: activeMenu === 'leads' ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                },
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 40, color: 'white' }}>
              <People />
            </ListItemIcon>
            {!sidebarCollapsed && <ListItemText primary="Leads" />}
          </ListItem>

          <ListItem 
            button 
            selected={activeMenu === 'messages'} 
            onClick={() => handleMenuClick('messages')}
            sx={{ 
              mb: 1,
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
              px: sidebarCollapsed ? 2 : 3,
              py: 1.5,
              borderRadius: sidebarCollapsed ? 0 : '0 25px 25px 0',
              backgroundColor: activeMenu === 'messages' ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                },
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 40, color: 'white' }}>
              <Message />
            </ListItemIcon>
            {!sidebarCollapsed && <ListItemText primary="Messages" />}
          </ListItem>
        </List>
        
        {/* Bottom Navigation */}
        <Box sx={{ mt: 'auto', mb: 2 }}>
          <ListItem 
            button 
            onClick={handleLogout}
            sx={{ 
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
              px: sidebarCollapsed ? 2 : 3,
              borderRadius: sidebarCollapsed ? 0 : '0 25px 25px 0',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              },
              mt: 1,
              py: 1.5,
            }}
          >
            <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 40, color: 'white' }}>
              <ExitToApp />
            </ListItemIcon>
            {!sidebarCollapsed && <ListItemText primary="Logout" />}
          </ListItem>
        </Box>
      </Box>
      
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { 
            xs: '100%', 
            sm: `calc(100% - ${sidebarWidth}px)` 
          },
          ml: { 
            xs: 0, 
            sm: `${sidebarWidth}px` 
          },
          backgroundColor: '#f5f8fb',
          minHeight: '100vh',
          pt: { xs: 8, sm: 9, md: 10 },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {activeMenu === 'dashboard' && (
          <>
            {/* Dashboard Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Leads
                    </Typography>
                    <Typography variant="h3">
                      {leads ? leads.length : 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Today's Leads
                    </Typography>
                    <Typography variant="h3">
                      {leads ? leads.filter(lead => {
                        const today = new Date();
                        const leadDate = new Date(lead.timestamp);
                        return leadDate.setHours(0,0,0,0) === today.setHours(0,0,0,0);
                      }).length : 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Messages
                    </Typography>
                    <Typography variant="h3">
                      {messages ? messages.reduce((total, chat) => total + (chat.messageCount || (chat.messages ? chat.messages.length : 0)), 0) : 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Charts & Graphs */}
            <Grid container spacing={3}>
              {/* Bar Chart for Leads vs Messages */}
              <Grid item xs={12} md={6}>
                <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff', p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Leads vs Messages</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={[
                        { name: 'Leads', value: leads ? leads.length : 0, fill: '#8884d8' },
                        { name: 'Messages', value: messages ? messages.reduce((total, chat) => total + (chat.messageCount || (chat.messages ? chat.messages.length : 0)), 0) : 0, fill: '#82ca9d' }
                      ]}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value" name="Count" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </Grid>

              {/* Pie Chart for Data Distribution */}
              <Grid item xs={12} md={6}>
                <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff', p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Data Distribution</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Leads', value: leads ? leads.length : 0, fill: '#8884D8' },
                          { name: 'Messages', value: messages ? messages.reduce((total, chat) => total + (chat.messageCount || (chat.messages ? chat.messages.length : 0)), 0) : 0, fill: '#82CA9D' }
                        ]}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      />
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </Grid>
            </Grid>
          </>
        )}

        {/* Filters Area */}
        {filterOpen && activeMenu !== 'messages' && (
          <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, email, phone..."
                  InputProps={{
                    startAdornment: <Search color="action" sx={{ mr: 1 }} />
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={dateFilter.startDate}
                  onChange={(e) => setDateFilter({...dateFilter, startDate: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <DateRange color="action" sx={{ mr: 1 }} />
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={dateFilter.endDate}
                  onChange={(e) => setDateFilter({...dateFilter, endDate: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <DateRange color="action" sx={{ mr: 1 }} />
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  startIcon={<Refresh />}
                  onClick={resetFilters}
                  variant="outlined"
                  sx={{ height: { xs: '48px', md: '56px' } }}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}
        
        {/* Leads Content */}
        {activeMenu === 'leads' && (
          <>
            {/* Lead Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Leads
                    </Typography>
                    <Typography variant="h4">
                      {leads.length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Today's Leads
                    </Typography>
                    <Typography variant="h4">
                      {leads.filter(lead => {
                        const today = new Date();
                        const leadDate = new Date(lead.timestamp);
                        return leadDate.setHours(0,0,0,0) === today.setHours(0,0,0,0);
                      }).length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Filtered Results
                    </Typography>
                    <Typography variant="h4">
                      {filteredLeads.length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
              <Box 
                sx={{ 
                  p: 2, 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: { xs: 1, sm: 0 }
                }}
              >
                <Typography variant="h6" component="h2">
                  Leads Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                  <Button 
                    variant="outlined" 
                    startIcon={<GetApp />}
                    onClick={exportToCSV}
                    size="small"
                    disabled={filteredLeads.length === 0 || loading}
                    sx={{ flexGrow: { xs: 1, sm: 0 } }}
                  >
                    Export CSV
                  </Button>
                  <Button 
                    variant="contained" 
                    startIcon={<Refresh />}
                    onClick={fetchData}
                    size="small"
                    sx={{ flexGrow: { xs: 1, sm: 0 }, '&:hover': {
                      backgroundColor: '#9D1680',
                    }  }}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Box sx={{ textAlign: 'center', my: 4, p: 2 }}>
                  <Typography color="error" gutterBottom>{error}</Typography>
                  <Button variant="contained" color="primary" onClick={fetchData} sx={{ mt: 2 }}>
                    Retry
                  </Button>
                </Box>
              ) : (
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Organization</TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Department</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>City</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>User Type</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLeads.length > 0 ? (
                        filteredLeads.map((lead) => {
                          const { month, day, year } = formatDate(lead.timestamp);
                          return (
                            <TableRow key={lead._id} hover>
                              <TableCell>{lead.name}</TableCell>
                              <TableCell>{lead.email}</TableCell>
                              <TableCell>{lead.phone}</TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{lead.organization}</TableCell>
                              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{lead.department}</TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{lead.city}</TableCell>
                              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{lead.userType}</TableCell>
                              <TableCell>
                                <Card sx={{ 
                                  width: 'fit-content', 
                                  textAlign: 'center',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  display: 'inline-flex',
                                  flexDirection: 'column',
                                  px: '10px',
                                  backgroundColor: '#f5f5f5'
                                }}>
                                  <Box sx={{ py: 0.2 }}>
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                      {month}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ py: 0.2 }}>
                                    <Typography variant="body2" fontWeight="bold">
                                      {day}
                                    </Typography>
                                  </Box>
                                  <Divider />
                                  <Box sx={{ py: 0.2 }}>
                                    <Typography variant="caption" fontWeight="medium" color="text.secondary">
                                      {year}
                                    </Typography>
                                  </Box>
                                </Card>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} align="center">No leads found</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}

        {/* Messages Content */}
        {activeMenu === 'messages' && (
          <>
            {/* Messages Section Content */}
            <Box id="messages-section-content">
              {/* Message Stats */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Total Conversations
                      </Typography>
                      <Typography variant="h4">
                        {messages.length}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Total Messages
                      </Typography>
                      <Typography variant="h4">
                        {messages.reduce((total, chat) => total + (chat.messageCount || 0), 0)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Average Messages Per Session
                      </Typography>
                      <Typography variant="h4">
                        {messages.length > 0 
                          ? (messages.reduce((total, chat) => total + (chat.messageCount || 0), 0) / messages.length).toFixed(1) 
                          : '0'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Add filtering for messages */}
              {filterOpen && (
                <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={10}>
                      <TextField
                        fullWidth
                        label="Search by Session ID"
                        value={messagesSearchQuery}
                        onChange={(e) => setMessagesSearchQuery(e.target.value)}
                        placeholder="Search by session ID..."
                        InputProps={{
                          startAdornment: <Search color="action" sx={{ mr: 1 }} />
                        }}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        startIcon={<Refresh />}
                        onClick={() => setMessagesSearchQuery('')}
                        variant="outlined"
                        sx={{ height: { xs: '48px', md: '56px' } }}
                      >
                        Reset
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
                <Box 
                  sx={{ 
                    p: 2, 
                    borderBottom: 1, 
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: { xs: 1, sm: 0 }
                  }}
                >
                  <Typography variant="h6" component="h2">
                    Chat Messages
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                    <Button 
                      variant="contained" 
                      startIcon={<Refresh />}
                      onClick={fetchMessages}
                      size="small"
                      sx={{ flexGrow: { xs: 1, sm: 0 }, '&:hover': {
                        backgroundColor: '#9D1680',
                      }  }}
                    >
                      Refresh
                    </Button>
                  </Box>
                </Box>
                
                {messagesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : messagesError ? (
                  <Box sx={{ textAlign: 'center', my: 4, p: 2 }}>
                    <Typography color="error" gutterBottom>{messagesError}</Typography>
                    <Button variant="contained" color="primary" onClick={fetchMessages} sx={{ mt: 2 }}>
                      Retry
                    </Button>
                  </Box>
                ) : messages.length === 0 ? (
                  <Box sx={{ textAlign: 'center', my: 4, p: 2 }}>
                    <Typography>No conversations found</Typography>
                  </Box>
                ) : (
                  <>
                    {/* Messages Table */}
                    <TableContainer>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Session ID</TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', textAlign: 'center' } }}>Message Count</TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', textAlign: 'center' } }}>Latest Message</TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', textAlign: 'center' } }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredMessages
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((chatSession, index) => {
                            // Get the latest message if available
                            const latestMessage = chatSession.messages && chatSession.messages.length > 0 
                              ? chatSession.messages[chatSession.messages.length - 1]
                              : null;
                            
                            // Format latest message text
                            const latestMessageText = latestMessage 
                              ? (latestMessage.userInput || latestMessage.aiResponse || '').substring(0, 50) + 
                                ((latestMessage.userInput && latestMessage.userInput.length > 50) || 
                                 (latestMessage.aiResponse && latestMessage.aiResponse.length > 50) ? '...' : '')
                              : 'No messages';

                            // Format latest message time  
                            const latestMessageTime = latestMessage?.timestamp 
                              ? formatChatDate(latestMessage.timestamp)
                              : '';

                            return (
                              <TableRow 
                                key={chatSession._id || index} 
                                hover
                              >
                                <TableCell>
                                  <Typography variant="body2">
                                    {chatSession.sessionId}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', textAlign: 'center' } }}  >{chatSession.messageCount || chatSession.messages?.length || 0}</TableCell>
                                <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', textAlign: 'center' } }}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                      {latestMessageText}
                                    </Typography>
                                    {latestMessageTime && (
                                      <Typography variant="caption" color="text.secondary">
                                        {latestMessageTime}
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', textAlign: 'center' } }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                    onClick={() => handleViewConversation(chatSession)}
                                  >
                                    View Conversation
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={filteredMessages.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)', backgroundColor: '#fff' }}
                    />
                  </>
                )}
              </Paper>
            </Box>

            {/* Conversation View - Hidden By Default */}
            <Box 
              id="conversation-view"
              sx={{ 
                display: 'none',
                flexDirection: 'column',
                width: '100%'
              }}
            >
              <Paper 
                sx={{ 
                  width: '100%', 
                  overflow: 'hidden', 
                  boxShadow: '0 2px 10px rgba(0,0,0,0.08)', 
                  borderRadius: 2, 
                  backgroundColor: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  height: { xs: '85vh', sm: '80vh' }
                }}
              >
                <Box sx={{ 
                  p: { xs: 1, sm: 2 }, 
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton 
                      size="small" 
                      onClick={handleBackFromConversation}
                      sx={{ mr: 1 }}
                    >
                      <ChevronLeft />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                      Conversation: {selectedConversation?.sessionId}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                      {selectedConversation && selectedConversation.messages && selectedConversation.messages.length > 0 
                        ? `${calculateChatDuration(selectedConversation.messages)} · ${formatChatDate(selectedConversation.messages[0].timestamp)}`
                        : ''}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={handleBackFromConversation}
                    >
                      <Close />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ 
                  p: { xs: 1, sm: 2, md: 3 }, 
                  flexGrow: 1, 
                  overflowY: 'auto',
                  backgroundColor: '#f5f8fb',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}>
                  {/* For debugging */}
                  <Box sx={{ display: 'none' }}>
                    {JSON.stringify(selectedConversation)}
                  </Box>
                  
                  {selectedConversation && selectedConversation.messages && selectedConversation.messages.map((msg, idx) => {
                    // Check if message has content
                    const hasUserInput = !!msg.userInput;
                    const hasAiResponse = !!msg.aiResponse;
                    
                    // If neither userInput nor aiResponse, skip this message
                    if (!hasUserInput && !hasAiResponse) {
                      return null;
                    }
                    
                    return (
                      <React.Fragment key={idx}>
                        {/* Render User Message */}
                        {hasUserInput && (
                          <Box 
                            sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-end',
                              maxWidth: '100%',
                              mb: hasAiResponse ? 0.5 : 2
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              backgroundColor: '#EDD3E7',
                              borderRadius: '12px',
                              padding: { xs: '8px 12px', sm: '12px 16px' },
                              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                              maxWidth: { xs: '90%', sm: '80%' },
                              position: 'relative',
                              '&:after': {
                                content: '""',
                                position: 'absolute',
                                bottom: '8px',
                                right: '-6px',
                                width: '0',
                                height: '0',
                                border: '8px solid transparent',
                                borderBottomColor: '#EDD3E7',
                                transform: 'rotate(-45deg)'
                              }
                            }}>
                              <Typography sx={{ 
                                fontSize: { xs: '0.7rem', sm: '0.75rem' }, 
                                color: 'text.secondary',
                                alignSelf: 'flex-end',
                                mb: 0.5
                              }}>
                                User · {formatChatDate(msg.timestamp)}
                              </Typography>
                              
                              <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                {msg.userInput}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        
                        {/* Render AI Response */}
                        {hasAiResponse && (
                          <Box 
                            sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              maxWidth: '100%',
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              backgroundColor: '#ffffff',
                              borderRadius: '12px',
                              padding: { xs: '8px 12px', sm: '12px 16px' },
                              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                              maxWidth: { xs: '90%', sm: '80%' },
                              position: 'relative',
                              '&:after': {
                                content: '""',
                                position: 'absolute',
                                bottom: '8px',
                                left: '-6px',
                                width: '0',
                                height: '0',
                                border: '8px solid transparent',
                                borderBottomColor: '#ffffff',
                                transform: 'rotate(45deg)'
                              }
                            }}>
                              <Typography sx={{ 
                                fontSize: { xs: '0.7rem', sm: '0.75rem' }, 
                                color: 'text.secondary',
                                alignSelf: 'flex-start',
                                mb: 0.5
                              }}>
                                Bot · {formatChatDate(msg.timestamp)}
                              </Typography>
                              
                              <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                {msg.aiResponse}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Box>
              </Paper>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

export default AdminPanel; 